import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Loading,
    Header,
    VehicleForm,
    SaleForm,
    SaleComplete,
    LastSales,
    ErrorModal,
    ConfirmModal,
    SuccessModal
} from '../components';
import { useApp } from '../contexts';
import {
    useSupplierNetwork,
    useVehicleSearch,
    useProducts,
    useBalance,
    useDriverSearch,
    useSaleTransaction,
    useTransactions,
    useXmlUpload,
    useReceiptPrint
} from '../hooks';
import { formatPlate, formatNumericInput, formatMoneyInput } from '../../shared/utils';
import { NotificationService } from '../../infrastructure/adapters';
import { Vehicle } from '../../domain/entities';

export const TerminalPage = () => {
    const { cnpj } = useParams<{ cnpj: string }>();
    const { errorModal, successModal, confirmModal, closeError, closeSuccess, closeConfirm } = useApp();

    const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
    const [showLastSales, setShowLastSales] = useState(false);
    const [saleFormKey, setSaleFormKey] = useState(0);

    const [plateInput, setPlateInput] = useState('');
    const [cardInput, setCardInput] = useState('');
    const [driverRegistration, setDriverRegistration] = useState('');
    const [selectedProductId, setSelectedProductId] = useState<number | null>(null);

    const { supplierNetwork, isLoading: isLoadingNetwork } = useSupplierNetwork(cnpj);
    const { vehicle, isLoading: isLoadingVehicle, searchVehicle, clearVehicle } = useVehicleSearch(supplierNetwork?.token || null);
    const { products, isLoading: isLoadingProducts } = useProducts(vehicle?.id || null, supplierNetwork?.token || null);
    const { transaction, setTransaction, updateOdometer, updateProduct, updateLiters, updateTotalValue, updateDriver, updatePassword, submitSale, resetForNewSale, resetAll, isProcessing } = useSaleTransaction(
        cnpj,
        supplierNetwork?.token || null,
        supplierNetwork?.id || null
    );
    const { balance, isLoading: isLoadingBalance } = useBalance(
        vehicle?.plate || null,
        selectedProductId,
        supplierNetwork?.token || null
    );
    const { driver, isLoading: isLoadingDriver } = useDriverSearch(
        driverRegistration,
        vehicle?.clientId || null,
        supplierNetwork?.token || null
    );
    const { transactions, reload: reloadTransactions } = useTransactions(supplierNetwork?.id || null, supplierNetwork?.token || null);
    const { uploadXml, xmlUploads } = useXmlUpload(supplierNetwork?.token || null);
    const { printReceipt } = useReceiptPrint(supplierNetwork?.id || null, supplierNetwork?.token || null);

    useEffect(() => {
        NotificationService.setSuccessCallback((message) => {
            useApp().showSuccess(message);
        });
        NotificationService.setErrorCallback((message) => {
            useApp().showError(message);
        });
    }, []);

    useEffect(() => {
        if (vehicle) {
            setTransaction(prev => new (prev.constructor as any)(
                vehicle,
                prev.product,
                prev.driver,
                prev.odometer,
                prev.liters,
                prev.totalValue,
                prev.password,
                prev.balance,
                prev.basePrice,
                prev.authorization,
                prev.document,
                prev.clientReceipt,
                prev.establishmentReceipt,
                prev.transactionId,
                prev.xmlAttached
            ));
        }
    }, [vehicle]);

    useEffect(() => {
        if (balance && selectedProductId) {
            setTransaction(prev => new (prev.constructor as any)(
                prev.vehicle,
                prev.product,
                prev.driver,
                prev.odometer,
                prev.liters,
                prev.totalValue,
                prev.password,
                balance,
                balance.basePrice,
                prev.authorization,
                prev.document,
                prev.clientReceipt,
                prev.establishmentReceipt,
                prev.transactionId,
                prev.xmlAttached
            ));
        }
    }, [balance, selectedProductId]);

    useEffect(() => {
        updateDriver(driver);
    }, [driver]);

    const handlePlateChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = formatPlate(e.target.value);
        if (value.length <= 7) {
            setPlateInput(value);
            setCardInput(value.length > 0 ? '' : cardInput);

            if (value.length >= 6 && value.length <= 7) {
                await searchVehicle(value);
            } else if (value.length < 6) {
                clearVehicle();
                setSelectedProductId(null);
            }
        }
    };

    const handleCardChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = formatNumericInput(e.target.value, 16);
        if (value.length <= 16) {
            setCardInput(value);
            setPlateInput(value.length > 0 ? '' : plateInput);

            if (value.length === 16) {
                await searchVehicle(value);
            } else if (value.length < 16) {
                clearVehicle();
                setSelectedProductId(null);
            }
        }
    };

    const handleNext = (e: React.FormEvent) => {
        e.preventDefault();
        if (vehicle) {
            setCurrentStep(2);
        }
    };

    const handleBack = () => {
        setSelectedProductId(null);
        setCurrentStep(1);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const success = await submitSale();
        if (success) {
            setCurrentStep(3);
            reloadTransactions();
        }
    };

    const handlePrintReceipt = async () => {
        const success = await printReceipt({
            transactionId: transaction.transactionId,
            fallbackReceiptText: transaction.clientReceipt,
            driverName: transaction.driver?.name,
            driverRegistration: transaction.driver?.registration,
        });
        if (success) {
            NotificationService.showPrintNotification();
        }
    };

    const handleEmailReceipt = () => {
        NotificationService.showEmailNotification();
    };

    const handleXmlUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && transaction.transactionId) {
            const success = await uploadXml(file, transaction.transactionId);
            if (success) {
                setTransaction(prev => new (prev.constructor as any)(
                    prev.vehicle,
                    prev.product,
                    prev.driver,
                    prev.odometer,
                    prev.liters,
                    prev.totalValue,
                    prev.password,
                    prev.balance,
                    prev.basePrice,
                    prev.authorization,
                    prev.document,
                    prev.clientReceipt,
                    prev.establishmentReceipt,
                    prev.transactionId,
                    true
                ));
            }
            reloadTransactions();
        }
        if (e.target) {
            e.target.value = '';
        }
    };

    const handleTransactionXmlUpload = async (transactionId: number, file: File) => {
        await uploadXml(file, transactionId);
        reloadTransactions();
    };

    const handleNewSaleSameVehicle = () => {
        resetForNewSale();
        setDriverRegistration('');
        setSelectedProductId(null);
        setSaleFormKey(prev => prev + 1);
        setCurrentStep(2);
    };

    const handleNewSale = () => {
        resetAll();
        setPlateInput('');
        setCardInput('');
        setDriverRegistration('');
        setSelectedProductId(null);
        clearVehicle();
        setCurrentStep(1);
    };

    if (!cnpj) {
        return (
            <div className="error-container">
                <div className="error-content">
                    <h2>CNPJ não encontrado</h2>
                    <p>A URL deve conter o CNPJ do fornecedor. Por favor, verifique o link de acesso.</p>
                </div>
            </div>
        );
    }

    if (isLoadingNetwork) {
        return <Loading />;
    }

    if (!supplierNetwork) {
        return (
            <div className="error-container">
                <div className="error-content">
                    <h2>Estamos com problemas técnicos</h2>
                    <p>Por favor, entre em contato com o suporte para obter assistência.</p>
                </div>
            </div>
        );
    }

    const saleData = {
        vehiclePlate: plateInput,
        vehicleCard: cardInput,
        clientName: vehicle?.clientName || 'Cliente não identificado',
        vehicleId: vehicle?.id || null,
        odometer: transaction.odometer,
        product: transaction.product?.name || '',
        productId: transaction.product?.id || null,
        liters: transaction.liters,
        totalValue: transaction.totalValue,
        driverRegistration,
        driverId: driver.id.toString(),
        driverName: driver.name,
        password: transaction.password,
        xmlContent: '',
        showFirstData: !!vehicle,
        balanceValue: transaction.balance?.value || null,
        balanceLiters: transaction.balance?.liters || null,
        basePrice: transaction.basePrice,
        clientId: vehicle?.clientId || null,
        authorization: transaction.authorization,
        document: transaction.document,
        clientReceipt: transaction.clientReceipt,
        establishmentReceipt: transaction.establishmentReceipt,
        transactionId: transaction.transactionId,
        xmlAttached: transaction.xmlAttached,
    };

    const supplierData = {
        name: supplierNetwork.name,
        cnpj: cnpj
    };

    return (
        <div className="terminal-container">
            <Header
                supplierData={supplierData}
                saleData={saleData}
                currentStep={currentStep}
                getFormattedCard={() => vehicle?.displayCard || cardInput}
                getPricePerLiter={currentStep === 3 ? () => transaction.formattedPricePerLiter : undefined}
                isLoadingVehicle={isLoadingVehicle}
            />

            {currentStep === 1 ? (
                <VehicleForm
                    saleData={saleData}
                    onPlateChange={handlePlateChange}
                    onCardChange={handleCardChange}
                    onNext={handleNext}
                    onClear={handleNewSale}
                    getFormattedCard={() => vehicle?.displayCard || cardInput}
                />
            ) : currentStep === 2 ? (
                <SaleForm
                    key={saleFormKey}
                    saleData={saleData}
                    onOdometerChange={(e) => updateOdometer(formatNumericInput(e.target.value, 8))}
                    onProductChange={(e) => {
                        const productId = e.target.value ? parseInt(e.target.value, 10) : null;
                        const selectedProduct = products.find(p => p.id === productId);
                        setSelectedProductId(productId);
                        updateProduct(selectedProduct || null);
                    }}
                    onLitersChange={(e) => updateLiters(formatMoneyInput(e.target.value))}
                    onTotalValueChange={(e) => updateTotalValue(formatMoneyInput(e.target.value))}
                    onDriverRegistrationChange={(e) => setDriverRegistration(formatNumericInput(e.target.value, 10))}
                    onPasswordChange={(e) => updatePassword(formatNumericInput(e.target.value, 6))}
                    onBack={handleBack}
                    onSubmit={handleSubmit}
                    getPricePerLiter={() => transaction.formattedPricePerLiter}
                    isPriceMatch={() => transaction.isPriceMatch}
                    isLoadingDriver={isLoadingDriver}
                    products={products}
                    isLoadingProducts={isLoadingProducts}
                    isLoadingBalance={isLoadingBalance}
                    getFormattedBalanceLiters={() => transaction.formattedBalanceLiters}
                    getFormattedBalanceValue={() => transaction.formattedBalanceValue}
                    isProcessingSale={isProcessing}
                />
            ) : (
                <SaleComplete
                    saleData={saleData}
                    onPrintReceipt={handlePrintReceipt}
                    onEmailReceipt={handleEmailReceipt}
                    onNewSaleSameVehicle={handleNewSaleSameVehicle}
                    onXmlUpload={handleXmlUpload}
                    onNewSale={handleNewSale}
                    getPricePerLiter={() => transaction.formattedPricePerLiter}
                    getFormattedCard={() => vehicle?.displayCard || cardInput}
                />
            )}

            <LastSales
                supplierId={supplierNetwork.id}
                token={supplierNetwork.token}
                showLastSales={currentStep === 1 || currentStep === 3 ? true : currentStep === 2 ? false : showLastSales}
                onToggle={currentStep === 1 || currentStep === 3 || currentStep === 2 ? () => { } : () => setShowLastSales(!showLastSales)}
                isAlwaysOpen={currentStep === 1 || currentStep === 3}
                onAddXml={handleTransactionXmlUpload}
                xmlUploads={xmlUploads}
            />

            <ErrorModal
                isOpen={errorModal.isOpen}
                message={errorModal.message}
                onClose={closeError}
            />

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm || (() => { })}
                onCancel={closeConfirm}
                confirmText="Continuar"
                cancelText="Cancelar"
            />

            <SuccessModal
                isOpen={successModal.isOpen}
                message={successModal.message}
                onClose={closeSuccess}
            />
        </div>
    );
};

